import { Container, type ContainerProps } from "@/components/layout/Container";
import { cn } from "@repo/design-system/utils";
import type { ComponentPropsWithoutRef } from "react";

/** The column's own right inset, matching `px-4`. */
export const BASE_PADDING_PX = 16;

/**
 * The right padding, from `md`, that keeps the column clear of the floating
 * buttons' strip (`FLOATING_BUTTONS_GUTTER_PX` from the viewport's right edge):
 * the strip's width minus the margin the centred column already leaves outside
 * itself once the space it centres in is wider than its ceiling, never less than
 * the base inset. A literal (spaces written as `_`) so Tailwind sees it; a test
 * pins it to `FLOATING_BUTTONS_GUTTER_PX`, `CONTAINER_WIDE_MAX_PX` and
 * {@link BASE_PADDING_PX} it is derived from.
 *
 * That space is not the viewport - the app shell's sidebar takes a slice of it -
 * so the maths reads `100cqw`, the width of the nearest size container, which
 * is {@link CartPageRoot}. Without one `cqw` falls back to the viewport, which is
 * wider than the space the columns centre in (by the sidebar's 56px), so the
 * padding would under-reserve by up to 28px.
 *
 * Below `md` the column keeps its plain `px-4`: the floating buttons ride above
 * the phone's bottom bar instead (see `useFloatingButtonsLift`).
 */
export const FLOATING_BUTTONS_PADDING_CLASS =
  "md:pr-[max(16px,calc(76px_-_max(0px,(100cqw_-_1400px)_/_2)))]";

/**
 * The size container {@link FLOATING_BUTTONS_PADDING_CLASS} measures against,
 * from `md` (the only place it is read). A literal so Tailwind sees it.
 */
export const FLOATING_BUTTONS_CONTAINER_CLASS =
  "md:[container-type:inline-size]";

/**
 * The root of every cart page state: the page's grey background and the size
 * container the {@link CartPageColumn}s inside it measure the space they centre
 * in against. Every state (loading skeleton, empty, filtered-empty, loaded,
 * error) renders this, so none can lose the container and drift off the shared
 * right edge.
 */
export function CartPageRoot({
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return (
    <div
      className={cn("bg-gray-50", FLOATING_BUTTONS_CONTAINER_CLASS, className)}
      {...props}
    />
  );
}

/**
 * The cart page's content column. Every state of the page - loading skeleton,
 * empty cart, filtered-empty, the List and Table views and the error state -
 * sits in this one column, so the content keeps the same left and right edges
 * as the page swaps between them instead of jumping between widths.
 *
 * Add a width or gutter here, not on a state, or the states drift apart again.
 * The gutter is `px-4` rather than the shared Container's `px-6` baseline: it is
 * the inset the cart page has always had, so the content lines up with the
 * header band and the toolbar card. A state that wants something narrower (the
 * empty and error cards) centres its own `max-w-*` block inside the column
 * rather than narrowing the column.
 *
 * From `md` the column also keeps its right edge clear of the floating support
 * buttons ({@link FLOATING_BUTTONS_PADDING_CLASS}). That is the column's own
 * behaviour, not an option: in the Table view lifting the buttons would put them
 * over grid cells, and every other state and the header share the column, so
 * they keep one right edge instead of jumping ~60px between states.
 */
export function CartPageColumn({
  className,
  ...props
}: Omit<ContainerProps, "size">) {
  return (
    <Container
      size="wide"
      className={cn("px-4", FLOATING_BUTTONS_PADDING_CLASS, className)}
      {...props}
    />
  );
}
